import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LockSimple, Buildings, Translate, Paperclip, GitBranch } from "@phosphor-icons/react";
import { ProjectIconEditor } from "@/zapi-frontend/shared/ui/ProjectIconEditor";
import { ProjectDimensionControl } from "@/zapi-frontend/shared/ui/ProjectDimensionControl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { useLanguages, LANGUAGE_CODE_TO_VALUE } from "@/zapi-frontend/shared/services/useLanguages";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useProjectThemeStore } from "@/zapi-frontend/stores/projectTheme";
import { ProjectMetaFlagButton } from "@/zapi-frontend/features/FlagDeficiency";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { FrameDocument } from "./FrameDocument";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

function SetupRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="flex items-center gap-[22px] py-4 w-full">
        <span className="w-[130px] shrink-0 text-[14px] leading-[13px] font-medium text-innoveraZinc-500">{label}</span>
        {children}
      </div>
      <div className="h-px w-full bg-innoveraZinc-150 last:hidden" />
    </>
  );
}

type TPillButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: React.ReactNode;
};

const PillButton = forwardRef<HTMLButtonElement, TPillButtonProps>(({ icon, children, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    {...rest}
    className={cn(
      "flex items-center gap-1 h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white",
      "text-[14px] leading-[13px] font-medium text-innoveraZinc-900 outline-none",
      rest.disabled ? "text-innoveraZinc-400" : "hover:border-innoveraZinc-200 transition-colors",
      className
    )}
  >
    {icon}
    {children}
  </button>
));
PillButton.displayName = "PillButton";

export function ProjectSetupIsland() {
  const { t } = useTranslation("projectHome");
  const { selectedProject, updateProject, fetchSelectedProject } = useProjectStore();
  const { data: dimensions = [] } = useProjectDimensions(undefined, selectedProject?.organizationId);
  const { openHandler } = useProjectSidebarStore();
  // Everything the project is built from is frozen while the analysis runs, so an
  // edit cannot land halfway through a generation that already consumed the old value.
  const { isSetupActive: isLocked } = useProjectGenerationState(selectedProject ? String(selectedProject.id) : "");

  const { languages } = useLanguages();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"organization" | "private">("organization");
  const [language, setLanguage] = useState("english");
  const [dimensionValues, setDimensionValues] = useState<Record<string, string>>({});
  const [hydratedProjectId, setHydratedProjectId] = useState<string | null>(null);
  const [iconShape, setIconShape] = useState<string | null>(null);
  const [iconColor, setIconColor] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [description]);

  const userEditedRef = useRef(false);
  const markEdited = () => {
    userEditedRef.current = true;
  };

  useEffect(() => {
    if (!selectedProject) return;
    if (hydratedProjectId === selectedProject.id && userEditedRef.current) return;
    setName(selectedProject.name ?? "");
    setDescription(selectedProject.shortDescription ?? "");
    setVisibility(selectedProject.visibility ?? "organization");
    setLanguage(selectedProject.language || "english");
    setDimensionValues(selectedProject.dimensionValues ?? {});
    userEditedRef.current = false;
    setHydratedProjectId(selectedProject.id);

    setIconShape(selectedProject.iconShape ?? null);
    setIconColor(selectedProject.iconColor ?? null);
  }, [selectedProject, hydratedProjectId]);

  const sortedDimensions = useMemo(() => [...dimensions].sort((a, b) => a.sortOrder - b.sortOrder), [dimensions]);

  const isDirty =
    !!selectedProject &&
    hydratedProjectId === selectedProject.id &&
    (name !== (selectedProject.name ?? "") ||
      description !== (selectedProject.shortDescription ?? "") ||
      visibility !== (selectedProject.visibility ?? "organization") ||
      language !== (selectedProject.language || "english") ||
      iconShape !== (selectedProject.iconShape ?? null) ||
      iconColor !== (selectedProject.iconColor ?? null) ||
      JSON.stringify(dimensionValues) !== JSON.stringify(selectedProject.dimensionValues ?? {}));

  const snapshot = JSON.stringify({ name, description, visibility, language, dimensionValues, iconShape, iconColor });
  const failedSnapshotRef = useRef<string | null>(null);
  const queuedSnapshotRef = useRef<string | null>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  useEffect(() => {
    if (!selectedProject || !isDirty || isSaving || !name.trim()) return;
    if (failedSnapshotRef.current === snapshot) return;
    const projectId = String(selectedProject.id);
    const timeout = setTimeout(async () => {
      setIsSaving(true);
      queuedSnapshotRef.current = snapshot;
      try {
        await updateProject(projectId, {
          name,
          description,
          visibility,
          language,
          dimensionValues,
          iconShape: iconShape ?? undefined,
          iconColor: iconColor ?? undefined
        });
        await fetchSelectedProject(projectId, true);
        failedSnapshotRef.current = null;
        if (snapshotRef.current === snapshot) userEditedRef.current = false;
      } catch {
        failedSnapshotRef.current = snapshot;
        toast.error("Failed to save project settings");
      } finally {
        setIsSaving(false);
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [
    selectedProject,
    isDirty,
    isSaving,
    snapshot,
    name,
    description,
    visibility,
    language,
    dimensionValues,
    iconShape,
    iconColor,
    updateProject,
    fetchSelectedProject
  ]);

  const latestRef = useRef({
    projectId: null as string | null,
    isDirty: false,
    snapshot,
    name,
    description,
    visibility,
    language,
    dimensionValues,
    iconShape,
    iconColor
  });
  latestRef.current = {
    projectId: selectedProject ? String(selectedProject.id) : null,
    isDirty,
    snapshot,
    name,
    description,
    visibility,
    language,
    dimensionValues,
    iconShape,
    iconColor
  };

  // Flush the pending debounced save on unmount so switching tabs doesn't drop edits
  useEffect(
    () => () => {
      const latest = latestRef.current;
      if (!latest.projectId || !latest.isDirty || !latest.name.trim()) return;
      if (queuedSnapshotRef.current === latest.snapshot) return;
      if (failedSnapshotRef.current === latest.snapshot) return;
      queuedSnapshotRef.current = latest.snapshot;
      const store = useProjectStore.getState();
      store
        .updateProject(latest.projectId, {
          name: latest.name,
          description: latest.description,
          visibility: latest.visibility,
          language: latest.language,
          dimensionValues: latest.dimensionValues,
          iconShape: latest.iconShape ?? undefined,
          iconColor: latest.iconColor ?? undefined
        })
        .then(() => useProjectStore.getState().fetchSelectedProject(latest.projectId as string, true))
        .catch(() => toast.error("Failed to save project settings"));
    },
    []
  );

  if (!selectedProject) return null;

  const documentsCount = selectedProject.documents?.length ?? 0;
  const languageName = (value: string, fallback?: string) => {
    if (value === "english") return t("projectSetup.languageName.english");
    if (value === "japanese") return t("projectSetup.languageName.japanese");
    return fallback ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");
  };
  const languageLabel = language ? languageName(language) : t("projectSetup.languageName.english");
  const languageOptions = languages.length
    ? languages
    : [
        { code: "en", label: "English" },
        { code: "jp", label: "Japanese" }
      ];

  return (
    <FrameDocument crumb={t("sidebar.projectSetup")}>
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-col gap-5 w-full">
            <ProjectIconEditor
              shape={iconShape}
              color={iconColor}
              logoUrl={selectedProject.logoUrl}
              readOnly={isLocked}
              onChange={(shape, color) => {
                markEdited();
                setIconShape(shape);
                setIconColor(color);
                useProjectThemeStore.getState().setPreviewIconColor(color);
              }}
            />
            <div className="group flex items-center gap-2 w-full">
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => {
                  markEdited();
                  setName(e.target.value);
                }}
                placeholder={t("projectSetup.namePlaceholder")}
                readOnly={isLocked}
                className="flex-1 min-w-0 text-[24px] leading-8 font-semibold text-innoveraZinc-900 outline-none bg-transparent placeholder:text-innoveraZinc-300"
              />
              <ProjectMetaFlagButton
                caseBriefId={selectedProject.caseBriefId}
                projectId={Number(selectedProject.id)}
                fieldLabel="Name"
                value={name}
                fieldRef={nameRef}
              />
            </div>
          </div>
          <div className="group flex items-start gap-2 w-full">
            <textarea
              ref={descriptionRef}
              value={description}
              onChange={(e) => {
                markEdited();
                setDescription(e.target.value);
              }}
              placeholder={t("projectSetup.descriptionPlaceholder")}
              readOnly={isLocked}
              rows={1}
              className="flex-1 min-w-0 resize-none text-[15px] leading-[23px] font-normal text-innoveraZinc-900 outline-none bg-transparent placeholder:text-innoveraZinc-300"
            />
            <ProjectMetaFlagButton
              caseBriefId={selectedProject.caseBriefId}
              projectId={Number(selectedProject.id)}
              fieldLabel="Description"
              value={description}
              fieldRef={descriptionRef}
            />
          </div>
        </div>

        <div className="flex flex-col w-full">
          <SetupRow label={t("projectSetup.visibility")}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <PillButton
                  disabled={isLocked}
                  icon={
                    visibility === "private" ? (
                      <LockSimple className="size-4" weight="regular" />
                    ) : (
                      <Buildings className="size-4" weight="regular" />
                    )
                  }
                >
                  {visibility === "private" ? t("projectSetup.private") : t("projectSetup.organization")}
                </PillButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={() => {
                    markEdited();
                    setVisibility("organization");
                  }}
                >
                  {t("projectSetup.organization")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    markEdited();
                    setVisibility("private");
                  }}
                >
                  {t("projectSetup.private")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SetupRow>

          <SetupRow label={t("projectSetup.language")}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <PillButton disabled={isLocked} icon={<Translate className="size-4" weight="regular" />}>
                  {languageLabel}
                </PillButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {languageOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.code}
                    onClick={() => {
                      markEdited();
                      setLanguage(LANGUAGE_CODE_TO_VALUE[option.code] ?? option.code);
                    }}
                  >
                    {languageName(LANGUAGE_CODE_TO_VALUE[option.code] ?? option.code, option.label)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SetupRow>

          <SetupRow label={t("projectSetup.version")}>
            <PillButton icon={<GitBranch className="size-4" weight="regular" />} disabled>
              {displayVersionName(selectedProject.versionDisplayName) || t("projectSetup.versionInitial")}
            </PillButton>
          </SetupRow>

          <SetupRow label={t("projectSetup.vault")}>
            <PillButton
              icon={<Paperclip className="size-4" weight="regular" />}
              disabled={isLocked}
              onClick={() => openHandler("vault")}
            >
              <span
                className={cn(
                  "flex items-center justify-center h-[18px] min-w-[21px] px-[5px] rounded bg-innoveraZinc-100 text-[14px] leading-[13px] font-medium",
                  isLocked ? "text-innoveraZinc-400" : "text-innoveraZinc-900"
                )}
              >
                {documentsCount}
              </span>
              {documentsCount === 1 ? t("projectSetup.attachment") : t("projectSetup.attachments")}
            </PillButton>
          </SetupRow>

          {sortedDimensions.map((dimension) => (
            <SetupRow key={dimension.id} label={dimension.name}>
              <ProjectDimensionControl
                dimension={dimension}
                value={dimensionValues[String(dimension.id)] ?? ""}
                onChange={(value) => {
                  markEdited();
                  setDimensionValues((prev) => ({ ...prev, [String(dimension.id)]: value }));
                }}
                disabled={isLocked}
              />
            </SetupRow>
          ))}
        </div>
      </div>
    </FrameDocument>
  );
}
